import { useNavigate } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { artworkApi } from '@/features/artworks/api/artworkApi'
import { ArtworkSearchResult } from '../api/adminApi'
import { cn } from '@/lib/utils'

interface ArtworkTileProps {
  artwork: ArtworkSearchResult
  className?: string
}

export function ArtworkTile({ artwork, className }: ArtworkTileProps) {
  const navigate = useNavigate()

  const statusColors = {
    DRAFT: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    PENDING: 'bg-blue-100 text-blue-800 border-blue-200',
    PUBLISHED: 'bg-green-100 text-green-800 border-green-200',
  }

  return (
    <Card
      className={cn(
        'group cursor-pointer overflow-hidden transition-all hover:shadow-lg hover:scale-[1.02]',
        className
      )}
      onClick={() => navigate(`/app/artworks/${artwork.id}`)}
    >
      {/* Artwork Image */}
      <div className="aspect-square relative overflow-hidden bg-muted">
        <img
          src={artworkApi.getArtworkImageUrl(artwork.id)}
          alt={artwork.name}
          className="h-full w-full object-cover transition-transform group-hover:scale-105"
          onError={(e) => {
            // Prevent infinite loop by checking if we're already showing the fallback
            if (e.currentTarget.src.includes('placeholder-image')) {
              return
            }
            e.currentTarget.src = '/placeholder-image.svg'
          }}
        />
        {/* Status Badge Overlay */}
        <div className="absolute top-2 right-2">
          <Badge
            variant="outline"
            className={cn(
              'text-xs font-medium',
              statusColors[artwork.status as keyof typeof statusColors]
            )}
          >
            {artwork.status}
          </Badge>
        </div>
      </div>

      {/* Artwork Info */}
      <div className="p-3 space-y-1">
        <h3 className="font-medium text-sm truncate" title={artwork.name}>
          {artwork.name}
        </h3>
        <p className="text-xs text-muted-foreground truncate">
          {artwork.artist?.artisticName ||
            `${artwork.artist?.firstName} ${artwork.artist?.lastName}`}
        </p>
        {artwork.description && (
          <p className="text-xs text-muted-foreground/70 line-clamp-2">
            {artwork.description}
          </p>
        )}
      </div>
    </Card>
  )
}
